import { cn } from '../../../shared/lib/cn';
import { Button } from '../../../shared/ui/button';
import { Note } from '../../../shared/ui/note';
import { SectionPanel } from '../../../shared/ui/section-panel';
import {
  outcomeCopy,
  subnetCount,
  type IOutcomeCopy,
  type TIpRangeRefresh,
} from '../../../entities/ip-range';

const TONE: Record<IOutcomeCopy['tone'], string> = {
  success: 'text-success',
  motion: 'text-motion',
  danger: 'text-danger',
};

const Cidrs = ({ sign, values }: { sign: '+' | '−'; values: string[] }) => (
  <div className="mt-2 flex flex-wrap gap-1">
    {values.map((value) => (
      <span
        key={value}
        className={cn(
          'rounded-tag px-label py-tight font-mono text-micro text-primary',
          sign === '+' ? 'bg-added-surface' : 'bg-removed-surface',
        )}
      >
        {sign} {value}
      </span>
    ))}
  </div>
);

const Outcome = ({ outcome }: { outcome: TIpRangeRefresh }) => {
  const copy = outcomeCopy(outcome);

  return (
    <div className="border-b border-border-sunken px-3 py-3 last:border-b-0">
      <div className="flex items-center gap-2">
        <span
          aria-hidden
          className={cn('size-dot shrink-0 rounded-full bg-current', TONE[copy.tone])}
        />
        <span className="text-data font-medium text-primary">{copy.label}</span>
        <span className="font-mono text-meta text-faint">{outcome.key}</span>
        <span className="ml-auto font-mono text-meta text-muted">
          v{outcome.version} · {subnetCount(outcome.cidrCount)}
        </span>
      </div>

      <p className="mt-1 max-w-prose-line text-meta leading-18 text-muted">{copy.detail}</p>

      {outcome.servingStale && (
        <Note className="mt-2">
          The list in force did not change. {subnetCount(outcome.cidrCount)} are still being matched
          against — the same ones as before this attempt. That is why the call came back a success:
          it did the right thing, it just did not bring anything new. The scheduled attempt will try
          again on its own.
        </Note>
      )}

      {outcome.added.length > 0 && <Cidrs sign="+" values={outcome.added} />}
      {outcome.removed.length > 0 && <Cidrs sign="−" values={outcome.removed} />}
    </div>
  );
};

export const RefreshOutcomes = ({
  outcomes,
  onDismiss,
}: {
  outcomes: TIpRangeRefresh[];
  onDismiss: () => void;
}) => (
  <SectionPanel
    title="What the refresh did"
    note={outcomes.length === 1 ? '1 list' : `${outcomes.length} lists`}
    actions={
      <Button variant="ghost" size="sm" onClick={onDismiss}>
        Dismiss
      </Button>
    }
  >
    {outcomes.map((outcome) => (
      <Outcome key={outcome.key} outcome={outcome} />
    ))}
  </SectionPanel>
);
